import React, { useEffect, useMemo, useState } from "react";
import StarRatings from "react-star-ratings";
import AnnouncementBar from "../MarketingPage/AnnouncementBar";
import Header from "../MarketingPage/Header";
import Lock from "@/assets/icons/lock";
import { useParams, useNavigate, Link } from "react-router-dom";
import { hasPlatformAccess } from "utils/platformAccess";
import {
  Page,
  StyledBreadCrumbs,
  PageTitleBar,
  PdpGrid,
  ImgPanel,
  ImgMain,
  MainImg,
  ImgPlaceholder,
  ImgMainLabel,
  ImgMainSublabel,
  ImgThumbs,
  ImgThumb,
  ProductPanel,
  ProductTitle,
  RatingRow,
  ReviewsText,
  DividerV,
  StockBadge,
  SupplierCard,
  SupplierCardHeader,
  SupplierTabLabel,
  SupplierVerified,
  SupplierMain,
  SupplierAvatar,
  SupplierName,
  SupplierMeta,
  SupplierMetrics,
  SupMetric,
  SupMetricVal,
  SupMetricLbl,
  PriceSection,
  PriceRow,
  PriceBox,
  PriceBoxLabel,
  PriceBoxVal,
  MarginBarWrap,
  MarginBarLabel,
  MarginBarTitle,
  MarginBarPct,
  MarginBar,
  MarginBarFill,
  MarginTip,
  QtyRow,
  QtyLabel,
  QtyControl,
  QtyBtn,
  QtyInput,
  ActionBtns,
  BtnCart,
  BtnShopify,
  BtnEbay,
  FeaturesSection,
  FeaturesLabel,
  FeaturesGrid,
  FeatItem,
  FeatIcon,
  FeatName,
  DescSection,
  DescHeader,
  DescTitle,
  DescToggle,
  DescGrid,
  DescBlock,
  DescBlockTitle,
  DescList,
  DescItem,
  StatsBar,
  StatItem,
  StatIconWrap,
  StatVal,
  StatLbl,
  OptionsBlock,
  StyledSelectContainer,
  StyledLabel,
  StyledSelect,
  LockHint,
  SignUpCta,
  accentGreen,
} from "./styles";
import { useGetProductDetailQuery } from "api/productApi";
import {
  usePostProductToShopifyMutation,
  useGetConnectedStoreQuery,
} from "api/shopifyApi";
import {
  usePostProductToEbayMutation,
  useGetConnectedEbayStoreQuery,
  useGetEbaySyncedProductsQuery,
} from "api/ebayApi";
import cache from "@/utils/cache";
import { STOCK_STATUS } from "@/utils/constants";
import Loader from "shared/Loader";
import { message } from "antd";
import SignInModal from "../MarketingPage/SignInModal";
import { useCartDrawer } from "context/CartDrawerContext";

const FEAT_ICONS = ["📹", "🎙️", "📶", "🌙", "🔧", "📦", "✨", "🔋", "📷"];

const items = [
  { title: <Link to="/home">Product</Link> },
  { title: <Link to="/best-selling-products">Best Selling Products</Link> },
  { title: <span>Product Detail</span> },
];

function formatMoney(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `$${Number(n).toFixed(2)}`;
}

function formatCompactMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}k`;
  return `$${num}`;
}

function parseDescriptionSections(desc) {
  if (!desc || !String(desc).trim()) return [];
  const trimmed = String(desc).trim();
  if (trimmed.includes("##")) {
    const raw = trimmed.split(/^##\s+/m).filter(Boolean);
    return raw.map((block) => {
      const lines = block.trim().split("\n");
      const title = lines[0].replace(/^#+\s*/, "").trim() || "Details";
      const body = lines.slice(1).join("\n").trim() || lines[0];
      const items = body
        .split(/\n+|•|–|-\s+/)
        .map((s) => s.replace(/^[•*-]\s*/, "").trim())
        .filter(Boolean);
      return { title, items: items.length ? items : [body] };
    });
  }
  const bullets = trimmed
    .split(/\n+/)
    .map((l) => l.replace(/^[•*-]\s*/, "").trim())
    .filter(Boolean);
  return [{ title: "Details", items: bullets.length ? bullets : [trimmed] }];
}

function pickKeyFeatures(sections) {
  const flat = sections.flatMap((s) => s.items);
  const unique = [...new Set(flat.map((t) => t.slice(0, 80)))];
  return unique.slice(0, 5).map((text, i) => ({
    icon: FEAT_ICONS[i % FEAT_ICONS.length],
    name: text.length > 48 ? `${text.slice(0, 45)}…` : text,
  }));
}

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);
  const [prices, setPrices] = useState({ price: null, msrp: null });
  const [activeImage, setActiveImage] = useState(0);
  const [descOpen, setDescOpen] = useState(true);
  const { data, isLoading } = useGetProductDetailQuery(id);
  const { data: connectedStore } = useGetConnectedStoreQuery();
  const { data: connectedEbayStore } = useGetConnectedEbayStoreQuery();
  const user = cache.getItem("user");
  const { data: ebaySyncedProducts = [] } = useGetEbaySyncedProductsQuery(
    undefined,
    { skip: !user }
  );
  const isPostedToEbay = ebaySyncedProducts.some(
    (item) => item.localProductId === id
  );
  const [postToShopify, { isLoading: isPostingToShopify }] =
    usePostProductToShopifyMutation();
  const [postToEbay, { isLoading: isPostingToEbay }] =
    usePostProductToEbayMutation();
  const { addToCart, cartItems } = useCartDrawer();

  const product = data?.product;
  const supplier = data?.supplier;

  useEffect(() => {
    if (product?.options) {
      const initialVariants = product.options.reduce((acc, option) => {
        acc[option.name] = option.values[0];
        return acc;
      }, {});
      setSelectedVariants(initialVariants);

      if (product?.variants?.length > 0) {
        const initialVariant = product.variants[0];
        setPrices({ price: initialVariant.price, msrp: initialVariant.MSRP });
      } else {
        setPrices({ price: product.price, msrp: product.MSRP });
      }
    }
  }, [product]);

  useEffect(() => {
    setActiveImage(0);
  }, [id, product?.id]);

  const mergedUrls = useMemo(
    () =>
      [
        ...(product?.images?.map((item) => item?.url) || []),
        ...(product?.variants?.map((item) => item?.image).filter(Boolean) || []),
      ].filter(Boolean),
    [product]
  );

  const metaData = product?.metaData || {};
  const {
    totalSales,
    GMV,
    totalInfluencers,
    totalComments,
    totalVideos,
    reviewCount,
    averageRating,
  } = metaData;

  const descSections = useMemo(
    () => parseDescriptionSections(product?.description),
    [product?.description]
  );

  const keyFeatures = useMemo(
    () => pickKeyFeatures(descSections),
    [descSections]
  );

  const costNum = prices.price != null ? Number(prices.price) : NaN;
  const msrpNum = prices.msrp != null ? Number(prices.msrp) : NaN;
  const profit =
    user && !Number.isNaN(costNum) && !Number.isNaN(msrpNum)
      ? msrpNum - costNum
      : null;
  const marginPct =
    user &&
      !Number.isNaN(msrpNum) &&
      msrpNum > 0 &&
      profit !== null &&
      !Number.isNaN(profit)
      ? Math.min(100, Math.max(0, (profit / msrpNum) * 100))
      : null;

  const ratingValue =
    averageRating != null ? Number(averageRating) : null;
  const reviewsLabel =
    reviewCount != null
      ? `(${reviewCount} Review${Number(reviewCount) === 1 ? "" : "s"})`
      : null;

  const { Option } = StyledSelect;

  if (isLoading) return <Loader isLoading={isLoading} />;
  if (!product) return <Loader isLoading />;

  const mainSrc = mergedUrls[activeImage];
  const categoryLabel = product.category?.name || "Product";
  const stockLabel = STOCK_STATUS[product.stock_status] || "Stock";
  const inStock = product.stock_status === "IN_STOCK";

  const onDecrease = () =>
    setQuantity((q) => Math.max(1, q - 1));
  const onIncrease = () => setQuantity((q) => q + 1);

  const handleVariants = (key, value) => {
    setSelectedVariants((prev) => ({ ...prev, [key]: value }));
    const selectedVariant = product.variants?.find((v) => v.name === value);
    if (selectedVariant) {
      setPrices({ price: selectedVariant.price, msrp: selectedVariant.MSRP });
    } else {
      setPrices({ price: product.price, msrp: product.MSRP });
    }
  };

  const toggleModal = () => setShowSignUpModal(!showSignUpModal);

  const handleAddToCart = () => {
    const variant = Object.values(selectedVariants).join(" / ");
    const finalVariant = product?.variants?.find((item) => item.name === variant);

    const availableStock = finalVariant
      ? finalVariant.totalStock
      : product?.totalStock;
    const isOutOfStock =
      !finalVariant && product?.stock_status === "OUT_OF_STOCK";

    if (isOutOfStock || availableStock <= 0) {
      message.error("This product is out of stock");
      return;
    }

    if (quantity > availableStock) {
      message.error(`Only ${availableStock} item(s) available in stock`);
      return;
    }

    const existingInCart = cartItems.find(
      (cartItem) =>
        cartItem.productId === product?.id &&
        (cartItem.variantId || null) === (finalVariant?.id || null)
    );
    const nextQuantity = Number(existingInCart?.productQuantity || 0) + quantity;

    if (nextQuantity > availableStock) {
      message.error(`Only ${availableStock} item(s) available in stock`);
      return;
    }

    const finalPayload = {
      productId: product?.id,
      productName: product?.name,
      productPrice: product?.price,
      productImage: product?.images?.[0]?.url,
      productQuantity: quantity,
      variantId: finalVariant?.id,
      variantPrice: finalVariant?.price,
    };
    addToCart(finalPayload);
    message.success("Product added to cart");
  };

  const handlePostToShopify = async () => {
    if (!connectedStore || !connectedStore.id) {
      message.warning("Please connect your Shopify store first");
      navigate("/profile?tab=connectedStores");
      return;
    }
    try {
      const shopifyProduct = {
        title: product.name,
        localProductId: product.id,
        body_html: product.description || "",
        vendor: "ecomdropship",
        product_type: product.category?.name || "",
        category: product.category?.name || "",
        status: "active",
        images: product.images?.map((img) => ({ src: img.url })) || [],
        variants:
          product.variants?.map((variant, index) => ({
            option1: `${product.name} ${index + 1}`,
            price: variant.MSRP ?? variant.price,
            sku: variant.sku || "",
            inventory_quantity: variant.totalStock || 0,
          })) || [
            {
              price: product.MSRP ?? product.price,
              inventory_quantity: product.totalStock || 0,
            },
          ],
      };
      const response = await postToShopify(shopifyProduct).unwrap();
      message.success(
        response?.message || "Product posted to Shopify successfully!"
      );
    } catch (error) {
      message.error(error?.data?.message || "Failed to post product to Shopify");
    }
  };

  const handlePostToEbay = async () => {
    if (!connectedEbayStore || !connectedEbayStore.id) {
      message.warning("Please connect your eBay account first");
      navigate("/profile?tab=connectedStores");
      return;
    }
    try {
      const ebayProduct = {
        title: product.name,
        localProductId: product.id,
        body_html: product.description || "",
        description: product.description || "",
        category: product.category?.name || "",
        price: String(prices.msrp ?? product.MSRP ?? prices.price ?? product.price),
        availableQuantity: product.totalStock ?? 1,
        totalStock: product.totalStock ?? 1,
        sku: product.sku || `ED-${product.id}`,
        images:
          product.images?.map((img) => ({ src: img.url, url: img.url })) || [],
        variants: product.variants?.length
          ? product.variants.map((v) => ({
            price: v.price,
            inventory_quantity: v.totalStock ?? 0,
          }))
          : [{ price: product.price, inventory_quantity: product.totalStock ?? 1 }],
      };
      const response = await postToEbay({
        product: ebayProduct,
        localProductId: product.id,
      }).unwrap();
      message.success(
        response?.message || "Product posted to eBay successfully!"
      );
    } catch (error) {
      message.error(error?.data?.message || "Failed to post product to eBay");
    }
  };

  const supplierMetaParts = [];
  if (supplier?.warehouseLabel) supplierMetaParts.push(supplier.warehouseLabel);
  if (supplier?.productCount != null)
    supplierMetaParts.push(`${supplier.productCount} products`);

  const supplierStats = supplier?.stats;
  const supplierStatToneColor = (tone) => {
    if (tone === "good") return accentGreen;
    if (tone === "warn") return "#E8521A";
    return "#9ca3af";
  };

  return (
    <>
      <AnnouncementBar />
      <Header />
      <Page>
        <StyledBreadCrumbs items={items} />
        <PageTitleBar>Product Detail</PageTitleBar>

        <PdpGrid>
          <ImgPanel>
            <ImgMain $hasImage={!!mainSrc}>
              {mainSrc ? (
                <MainImg src={mainSrc} alt="" />
              ) : (
                <ImgPlaceholder aria-hidden>📦</ImgPlaceholder>
              )}
              <ImgMainLabel>
                {product.name.slice(0, 42)}
                {product.name.length > 42 ? "…" : ""}
                <ImgMainSublabel>{categoryLabel}</ImgMainSublabel>
              </ImgMainLabel>
            </ImgMain>
            {mergedUrls.length > 0 && (
              <ImgThumbs>
                {mergedUrls.map((url, i) => (
                  <ImgThumb
                    key={url + i}
                    type="button"
                    $active={i === activeImage}
                    onClick={() => setActiveImage(i)}
                    aria-label={`Image ${i + 1}`}
                  >
                    <img src={url} alt="" />
                  </ImgThumb>
                ))}
              </ImgThumbs>
            )}
          </ImgPanel>

          <ProductPanel>
            <ProductTitle>{product.name}</ProductTitle>

            <RatingRow>
              {ratingValue != null && !Number.isNaN(ratingValue) && (
                <>
                  <StarRatings
                    rating={Math.min(5, Math.max(0, ratingValue))}
                    starRatedColor="#f59e0b"
                    starEmptyColor="#e5e7eb"
                    numberOfStars={5}
                    starDimension="14px"
                    starSpacing="2px"
                    name="rating"
                  />
                  {reviewsLabel && <ReviewsText>{reviewsLabel}</ReviewsText>}
                  <DividerV />
                </>
              )}
              <StockBadge>
                {inStock ? "✓ " : ""}
                {stockLabel}
              </StockBadge>
            </RatingRow>

            {supplier && (
              <SupplierCard>
                <SupplierCardHeader>
                  <SupplierTabLabel>Dropship Supplier</SupplierTabLabel>
                  <SupplierVerified $muted={!supplier.verified}>
                    {supplier.verified
                      ? "✓ Platform Verified"
                      : "Not verified"}
                  </SupplierVerified>
                </SupplierCardHeader>
                <SupplierMain>
                  <SupplierAvatar
                    style={
                      supplier.profileUrl
                        ? {
                          backgroundImage: `url(${supplier.profileUrl})`,
                          backgroundColor: "transparent",
                        }
                        : undefined
                    }
                  >
                    {!supplier.profileUrl ? supplier.initials : ""}
                  </SupplierAvatar>
                  <div>
                    <SupplierName>{supplier.displayName}</SupplierName>
                    <SupplierMeta>
                      {supplierMetaParts.join(" · ") || "Independent supplier"}
                    </SupplierMeta>
                  </div>
                </SupplierMain>
                <SupplierMetrics>
                  <SupMetric>
                    <SupMetricVal
                      $tone={supplierStatToneColor(
                        supplierStats?.onTimeShip?.tone || "neutral"
                      )}
                    >
                      {supplierStats?.onTimeShip?.display ?? "—"}
                    </SupMetricVal>
                    <SupMetricLbl>On-Time Ship</SupMetricLbl>
                  </SupMetric>
                  <SupMetric>
                    <SupMetricVal
                      $tone={supplierStatToneColor(
                        supplierStats?.trackingAccuracy?.tone || "neutral"
                      )}
                    >
                      {supplierStats?.trackingAccuracy?.display ?? "—"}
                    </SupMetricVal>
                    <SupMetricLbl>Tracking Acc.</SupMetricLbl>
                  </SupMetric>
                  <SupMetric>
                    <SupMetricVal
                      $tone={supplierStatToneColor(
                        supplierStats?.cancelRate?.tone || "neutral"
                      )}
                    >
                      {supplierStats?.cancelRate?.display ?? "—"}
                    </SupMetricVal>
                    <SupMetricLbl>Cancel Rate</SupMetricLbl>
                  </SupMetric>
                  <SupMetric>
                    <SupMetricVal
                      $tone={supplierStatToneColor(
                        supplierStats?.processing?.tone || "neutral"
                      )}
                    >
                      {supplierStats?.processing?.display ?? "—"}
                    </SupMetricVal>
                    <SupMetricLbl>Processing</SupMetricLbl>
                  </SupMetric>
                </SupplierMetrics>
              </SupplierCard>
            )}

            <PriceSection>
              <PriceRow>
                <PriceBox>
                  <PriceBoxLabel>Your Cost</PriceBoxLabel>
                  {user ? (
                    <PriceBoxVal>{formatMoney(prices.price)}</PriceBoxVal>
                  ) : (
                    <LockHint>
                      <Lock />
                    </LockHint>
                  )}
                </PriceBox>
                <PriceBox>
                  <PriceBoxLabel>MSRP / Sell Price</PriceBoxLabel>
                  <PriceBoxVal $color={accentGreen}>
                    {formatMoney(prices.msrp)}
                  </PriceBoxVal>
                </PriceBox>
                <PriceBox>
                  <PriceBoxLabel>Est. Profit</PriceBoxLabel>
                  {user && profit !== null && !Number.isNaN(profit) ? (
                    <PriceBoxVal $color={accentGreen}>
                      {profit >= 0 ? "+" : ""}
                      {formatMoney(profit)}
                    </PriceBoxVal>
                  ) : (
                    <LockHint>
                      <Lock />
                    </LockHint>
                  )}
                </PriceBox>
              </PriceRow>
              {user && marginPct !== null && (
                <MarginBarWrap>
                  <MarginBarLabel>
                    <MarginBarTitle>Profit Margin</MarginBarTitle>
                    <MarginBarPct>
                      {marginPct.toFixed(0)}% margin
                    </MarginBarPct>
                  </MarginBarLabel>
                  <MarginBar>
                    <MarginBarFill $pct={marginPct} />
                  </MarginBar>
                </MarginBarWrap>
              )}
              <MarginTip>
                Tip: Price competitively using MSRP as a ceiling; your cost is
                your floor for healthy margins.
              </MarginTip>
            </PriceSection>

            {product.options?.length > 0 && (
              <OptionsBlock>
                {product.options.map((option) => (
                  <StyledSelectContainer key={option?.id}>
                    <StyledLabel>{option?.name}:</StyledLabel>
                    <StyledSelect
                      size="large"
                      value={
                        selectedVariants[option?.name] || option?.values?.[0]
                      }
                      onChange={(value) => handleVariants(option?.name, value)}
                    >
                      {option?.values?.map((value, index) => (
                        <Option key={index} value={value}>
                          {value}
                        </Option>
                      ))}
                    </StyledSelect>
                  </StyledSelectContainer>
                ))}
              </OptionsBlock>
            )}

            <QtyRow>
              <QtyLabel>Quantity</QtyLabel>
              <QtyControl>
                <QtyBtn type="button" onClick={onDecrease}>
                  −
                </QtyBtn>
                <QtyInput
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setQuantity(Number.isNaN(v) ? 1 : Math.max(1, v));
                  }}
                />
                <QtyBtn type="button" onClick={onIncrease}>
                  +
                </QtyBtn>
              </QtyControl>
            </QtyRow>

            {keyFeatures.length > 0 && (
              <FeaturesSection>
                <FeaturesLabel>Key Features</FeaturesLabel>
                <FeaturesGrid>
                  {keyFeatures.map((f, i) => (
                    <FeatItem key={i}>
                      <FeatIcon>{f.icon}</FeatIcon>
                      <FeatName>{f.name}</FeatName>
                    </FeatItem>
                  ))}
                </FeaturesGrid>
              </FeaturesSection>
            )}

            {descSections.length > 0 && (
              <DescSection>
                <DescHeader>
                  <DescTitle>Product Description</DescTitle>
                  <DescToggle
                    type="button"
                    onClick={() => setDescOpen((o) => !o)}
                  >
                    {descOpen ? "Show less ↑" : "Show more ↓"}
                  </DescToggle>
                </DescHeader>
                {descOpen && (
                  <DescGrid>
                    {descSections.map((sec, si) => (
                      <DescBlock key={si}>
                        <DescBlockTitle>{sec.title}</DescBlockTitle>
                        <DescList>
                          {sec.items.map((line, li) => (
                            <DescItem key={li}>{line}</DescItem>
                          ))}
                        </DescList>
                      </DescBlock>
                    ))}
                  </DescGrid>
                )}
              </DescSection>
            )}

            <StatsBar>
              <StatItem>
                <StatIconWrap>💰</StatIconWrap>
                <StatVal>{formatCompactMoney(totalSales)}</StatVal>
                <StatLbl>Total Sales</StatLbl>
              </StatItem>
              <StatItem>
                <StatIconWrap>📈</StatIconWrap>
                <StatVal>{GMV != null ? GMV : "—"}</StatVal>
                <StatLbl>GMV</StatLbl>
              </StatItem>
              <StatItem>
                <StatIconWrap>👥</StatIconWrap>
                <StatVal>
                  {totalInfluencers != null ? totalInfluencers : "—"}
                </StatVal>
                <StatLbl>Influencers</StatLbl>
              </StatItem>
              <StatItem>
                <StatIconWrap>🎬</StatIconWrap>
                <StatVal>
                  {totalVideos != null ? totalVideos : "—"}
                </StatVal>
                <StatLbl>Total Videos</StatLbl>
              </StatItem>
              <StatItem>
                <StatIconWrap>💬</StatIconWrap>
                <StatVal>
                  {totalComments != null ? totalComments : "—"}
                </StatVal>
                <StatLbl>Comments</StatLbl>
              </StatItem>
            </StatsBar>
            {user && hasPlatformAccess(user) ? (
              <ActionBtns>
                <BtnCart type="button" onClick={handleAddToCart}>
                  Add to Cart
                </BtnCart>
                <BtnShopify
                  type="button"
                  onClick={handlePostToShopify}
                  disabled={isPostingToShopify}
                >
                  <svg width="14" height="16" viewBox="0 0 109 124" fill="none">
                    <path
                      d="M95.2 23.4c-.1-.7-.7-1.1-1.2-1.1s-10.3-.8-10.3-.8-6.9-6.8-7.6-7.5c-.7-.7-2.1-.5-2.6-.3l-3.8 1.2C67.4 8.3 63.4 2.3 56.4 2.3h-.6C53.9 1 52 .2 50.3.2 37.6.2 31.5 15.4 29.6 23.1c-4.9 1.5-8.4 2.6-8.8 2.7-2.7.9-2.8 1-3.1 3.5C17.4 31.3 8 105 8 105l72.2 13.5 31.2-7.8S95.3 24.1 95.2 23.4z"
                      fill="white"
                      opacity="0.9"
                    />
                  </svg>
                  {connectedStore?.id
                    ? "Post to Shopify"
                    : "Connect Shopify"}
                </BtnShopify>
                <BtnEbay
                  type="button"
                  onClick={handlePostToEbay}
                  disabled={isPostingToEbay}
                >
                  <svg width="28" height="16" viewBox="0 0 100 40" fill="none">
                    <text x="0" y="32" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="38">
                      <tspan fill="#fff">e</tspan>
                      <tspan fill="#0064D2">b</tspan>
                      <tspan fill="#F5AF02">a</tspan>
                      <tspan fill="#86B817">y</tspan>
                    </text>
                  </svg>
                  {isPostedToEbay
                    ? "Posted to eBay"
                    : connectedEbayStore?.id
                      ? "Post to eBay"
                      : "Connect eBay"}
                </BtnEbay>
              </ActionBtns>
            ) : user ? (
              <ActionBtns>
                <BtnShopify
                  type="button"
                  onClick={handlePostToShopify}
                  disabled={isPostingToShopify}
                >
                  <svg width="14" height="16" viewBox="0 0 109 124" fill="none">
                    <path
                      d="M95.2 23.4c-.1-.7-.7-1.1-1.2-1.1s-10.3-.8-10.3-.8-6.9-6.8-7.6-7.5c-.7-.7-2.1-.5-2.6-.3l-3.8 1.2C67.4 8.3 63.4 2.3 56.4 2.3h-.6C53.9 1 52 .2 50.3.2 37.6.2 31.5 15.4 29.6 23.1c-4.9 1.5-8.4 2.6-8.8 2.7-2.7.9-2.8 1-3.1 3.5C17.4 31.3 8 105 8 105l72.2 13.5 31.2-7.8S95.3 24.1 95.2 23.4z"
                      fill="white"
                      opacity="0.9"
                    />
                  </svg>
                  {connectedStore?.id
                    ? "Post to Shopify"
                    : "Connect Shopify"}
                </BtnShopify>
                <SignUpCta type="button" onClick={() => navigate("/pricing")}>
                  Subscribe To Buy Product
                </SignUpCta>
              </ActionBtns>
            ) : (
              <SignUpCta type="button" onClick={toggleModal}>
                Sign Up to Unlock Product Cost
              </SignUpCta>
            )}
          </ProductPanel>
        </PdpGrid>
      </Page>

      {showSignUpModal && (
        <SignInModal
          isModalOpen={showSignUpModal}
          toggleModal={toggleModal}
          isSignUpMode={isSignUp}
          onSwitchMode={() => setIsSignUp((prev) => !prev)}
        />
      )}
    </>
  );
};

export default ProductDetail;
